import { Cart as PepCart } from "../../components/cart/Cart";

import { cart as cartConfig, endpoints, setDYContext } from "config";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";

import { PAGE_TYPE_ENUM } from "@shared/types";
import { CmsProductGtinWithSlug } from "components/cart/common/types";
import { EmptyCart } from "components/cart/EmptyCart";
import PageLayout from "components/layout/PageLayout";
import { Spinner } from "components/spinner/Spinner";
import { useLogContext } from "context/log";
import { UserContext } from "context/user";
import {
  generatePageId,
  getAnalyticsCart,
  logCartView,
} from "helpers/analyticsLogger";
import { setCachingHeader } from "helpers/cacheHeader";
import { cartHasSubscriptionItem, getCheckoutUrl } from "helpers/cart";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useState } from "react";
import { createDatoClient } from "services/datocms";
import {
  CartPageQuery,
  getSdk,
  ProductsByGtinAndBundleTagQuery,
} from "services/datocms/generated";
import { createQueryParamsForCaching } from "utils/create-query-params";

interface CartPageProps {
  datoCartPage: CartPageQuery["cartPage"];
}

export const getServerSideProps: GetServerSideProps<CartPageProps> = async (
  appContext,
) => {
  setCachingHeader(appContext);

  try {
    const { cartPage } = await getSdk(
      createDatoClient(
        appContext.preview,
        createQueryParamsForCaching({ queryType: "CartPage" }),
      ),
    ).CartPage();
    return {
      props: {
        datoCartPage: cartPage,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export default function Cart({ datoCartPage }: CartPageProps): JSX.Element {
  const [products, setProducts] = useState<
    ProductsByGtinAndBundleTagQuery["allProducts"]
  >([]);

  const [isGoToCheckoutLoading, setIsGoToCheckoutLoading] = useState(false);
  const router = useRouter();

  const cmsProducts = useMemo(() => {
    const cmsProducts: CmsProductGtinWithSlug[] = [];
    products?.forEach((product) => {
      if (product.cylinderTypes.length) {
        product.cylinderTypes.forEach((cylinder) => {
          cmsProducts.push({
            ...product,
            gtin: cylinder.gtin as string,
          });
        });
      } else {
        let bundleTags: string[] = [];
        if (product.productBundles.length) {
          bundleTags = product.productBundles.map((bundle) => {
            return bundle.bundleId as string;
          });
        }
        if (product.bundleTag) {
          bundleTags.push(product.bundleTag as string);
        }
        cmsProducts.push({
          ...product,
          bundleTags,
        });
      }
    });
    return cmsProducts;
  }, [products]);

  const { tenant } = useContext(AppContext);
  const { cart, loading, getSize } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);

  const { useLogPageView } = useLogContext();

  useEffect(() => {
    const fetchProducts = async () => {
      if (!cart?.lineItems || cart.lineItems.length === 0) {
        return;
      }

      const gtins: string[] = [];
      const bundleTags: string[] = [];

      cart.lineItems.forEach((lineItem) => {
        if (lineItem.__typename === "BundleLineItem") {
          bundleTags.push(lineItem.dynamicBundle.tag);
        } else if (lineItem.__typename === "CartItem") {
          gtins.push(lineItem.item.gtin);
        }
      });

      const { allProducts } = await getSdk(
        createDatoClient(
          router.isPreview,
          createQueryParamsForCaching({ queryType: "AllProducts" }),
        ),
      ).ProductsByGtinAndBundleTag({
        gtins,
        bundleTags,
      });

      setProducts(allProducts);
    };
    fetchProducts();
  }, [router.isPreview, cart?.lineItems]);

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.cart,
    id: generatePageId("cart"),
  });

  useEffect(() => {
    if (cart && products.length > 0) {
      logCartView(currentUserId, getAnalyticsCart(cart, products));
    }
  }, [cart, products, currentUserId]);

  useEffect(() => {
    setDYContext(
      "CART",
      cart?.lineItems?.map((lineItem) => {
        if (lineItem.__typename === "BundleLineItem") {
          return lineItem.bundleId;
        } else if (lineItem.__typename === "CartItem") {
          return lineItem.item.gtin;
        }
      }),
    );
  }, [cart?.lineItems]);

  const checkoutUrl = getCheckoutUrl();
  const size = getSize();

  const { maxCartQty: maxCartQtyDefault } = cartConfig;
  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;

  const minPurchaseAmt = tenant?.shoppingBagConfig?.minimumPurchaseAmount;
  const freeShippingThreshold = tenant?.shippingConfig?.freeShippingThreshold;

  const goToCheckout = () => {
    if (!cart) return;
    // logCartCheckoutButtonClickEvent(currentUserId, cart);

    if (currentUserId === null && cartHasSubscriptionItem(cart)) {
      const modal = "CHECKOUT_SUBSCRIPTION_MODAL";
      open(modal);
    } else {
      setIsGoToCheckoutLoading(true);
      window.open(checkoutUrl, "_self");
    }
  };

  if (loading) {
    return (
      <PageLayout
        seo={datoCartPage?.seo}
        className="min-h-screen flex items-center justify-center"
      >
        <Spinner dotSize="25px" />
      </PageLayout>
    );
  }

  return (
    <PageLayout seo={datoCartPage?.seo}>
      <main role="main">
        {size === 0 || !cart ? (
          <EmptyCart components={datoCartPage?.components} />
        ) : (
          <PepCart
            cart={cart}
            checkoutImages={datoCartPage?.checkoutPaymentImages ?? []}
            cmsProducts={cmsProducts}
            currentUserId={currentUserId}
            freeShippingThreshold={freeShippingThreshold}
            goToCheckout={goToCheckout}
            maxCartQty={maxCartQty}
            minPurchaseAmt={minPurchaseAmt}
            productsImgCdnEndpoint={endpoints.productsImgCdn}
            size={size}
            isGoToCheckoutLoading={isGoToCheckoutLoading}
          />
        )}
      </main>
    </PageLayout>
  );
}
